import {
  ALFA_BANK_ID,
  AMOBILE_BANK_ID,
  OZON_BANK_ID,
  RAIFFEISEN_BANK_ID,
  SBER_BANK_ID,
  SOVCOM_BANK_ID,
  SOLIDARNOST_BANK_ID,
  T_BANK_ID,
  VTB_BANK_ID,
  VASL_BANK,
  AKB_BANK,
  SPITAMEN_BANK,
  AMERIABANK_ID,
  MATIN_BANK,
  M10_BANK,
  BIRBANK,
  IDRAM_BANK,
  YANDEX_BANK_ID,
  WILDBERRIES_BANK_ID,
} from '@/entities/banks'
import {
  formatBankAccount,
  formatCardNumber,
  formatPhoneNumber,
  NewRequisite,
} from '@/features/requisites/model'
import { useCallback, useMemo, useReducer } from 'react'
import { UseFormSetValue } from 'react-hook-form'


type BankId =
  | typeof AMOBILE_BANK_ID
  | typeof SBER_BANK_ID
  | typeof OZON_BANK_ID
  | typeof T_BANK_ID
  | typeof ALFA_BANK_ID
  | typeof VTB_BANK_ID
  | typeof RAIFFEISEN_BANK_ID
  | typeof VASL_BANK
  | typeof AKB_BANK
  | typeof SPITAMEN_BANK
  | typeof AMERIABANK_ID
  | typeof SOVCOM_BANK_ID
  | typeof MATIN_BANK
  | typeof M10_BANK
  | typeof BIRBANK
  | typeof IDRAM_BANK
  | typeof YANDEX_BANK_ID
  | typeof SOLIDARNOST_BANK_ID
  | typeof WILDBERRIES_BANK_ID

type RequisiteField = 'phone' | 'account' | 'cards' | 'sbpPaymentLink';
interface BankConfig {
  requiredFields: RequisiteField[];
  optionalFields: RequisiteField[];
  maxCards: number;
  requiresAccount: boolean;
}
// Типы полей реквизитов
const REQUISITE_TYPES = {
  PHONE: 1,
  CARD: 100,
  ACCOUNT: 200,
} as const

// Конфигурация банков
const BANK_CONFIGS: { [key in BankId]: BankConfig } = {
  [AMOBILE_BANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
  [SBER_BANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: ['account', 'cards'],
    maxCards: Infinity,
    requiresAccount: false, // Согласно комментарию в коде
  },
  // Остальные банки имеют одинаковую конфигурацию
  [OZON_BANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: ['sbpPaymentLink'],
    maxCards: 1,
    requiresAccount: false,
  },
  [T_BANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
  [ALFA_BANK_ID]: {
    requiredFields: ['phone'],
      optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
  [VTB_BANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
  [RAIFFEISEN_BANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
  [SOVCOM_BANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
  [VASL_BANK]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 3,
    requiresAccount: false,
  },
  [SPITAMEN_BANK]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
  [AMERIABANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
  [AKB_BANK]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
  [MATIN_BANK]: {
    requiredFields: ['cards'],
    optionalFields: [],
    maxCards: 3,
    requiresAccount: false,
  },
  [M10_BANK]: {
    requiredFields: ['phone'],
    optionalFields: [],
    maxCards: 0,
    requiresAccount: false,
  },
  [YANDEX_BANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: [],
    maxCards: 0,
    requiresAccount: false,
  },
  [WILDBERRIES_BANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: [],
    maxCards: 0,
    requiresAccount: false,
  },
  [BIRBANK]: {
    requiredFields: ['cards'],
    optionalFields: [],
    maxCards: 3,
    requiresAccount: false,
  },
  [IDRAM_BANK]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
  [SOLIDARNOST_BANK_ID]: {
    requiredFields: ['phone'],
    optionalFields: ['cards'],
    maxCards: 1,
    requiresAccount: false,
  },
} as const

// Интерфейсы
interface FieldError {
  message?: string
}

interface FormState {
  selectedBankId: string | null
  showAdditionalFields: boolean
  phone: string
  account: string
  cards: string[]
  sbpPaymentLink: string
  errors: {
    phone?: FieldError
    account?: FieldError
    cards: (FieldError | undefined)[]
  }
}

// Actions для reducer
type FormAction =
  | { type: 'SELECT_BANK'; bankId: string }
  | { type: 'RESET_FORM' }
  | { type: 'SET_PHONE'; value: string }
  | { type: 'SET_ACCOUNT'; value: string }
  | { type: 'SET_CARD'; index: number; value: string }
  | { type: 'SET_SBP_PAYMENT_LINK'; value: string }
  | { type: 'ADD_CARD' }
  | { type: 'REMOVE_CARD'; index: number }
  | { type: 'SET_PHONE_ERROR'; error?: FieldError }
  | { type: 'SET_ACCOUNT_ERROR'; error?: FieldError }
  | { type: 'SET_CARD_ERROR'; index: number; error?: FieldError }

// Начальное состояние
const initialState: FormState = {
  selectedBankId: null,
  showAdditionalFields: false,
  phone: '+ ',
  account: '',
  cards: [],
  sbpPaymentLink: '',
  errors: {
    phone: undefined,
    account: undefined,
    cards: [],
  },
}

// Reducer для управления состоянием
function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SELECT_BANK':
      return {
        ...initialState,
        selectedBankId: action.bankId,
        showAdditionalFields: true,
        phone: '+ ',
      }

    case 'RESET_FORM':
      return initialState

    case 'SET_PHONE':
      return {
        ...state,
        phone: action.value,
      }

    case 'SET_ACCOUNT':
      return {
        ...state,
        account: action.value,
      }

    case 'SET_CARD':
      return {
        ...state,
        cards: state.cards.map((card, index) =>
          index === action.index ? action.value : card
        ),
      }

    case 'SET_SBP_PAYMENT_LINK':
      return {
        ...state,
        sbpPaymentLink: action.value,
      }

    case 'ADD_CARD':
      return {
        ...state,
        cards: [...state.cards, ''],
        errors: {
          ...state.errors,
          cards: [...state.errors.cards, undefined],
        },
      }

    case 'REMOVE_CARD':
      return {
        ...state,
        cards: state.cards.filter((_, index) => index !== action.index),
        errors: {
          ...state.errors,
          cards: state.errors.cards.filter((_, index) => index !== action.index),
        },
      }

    case 'SET_PHONE_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          phone: action.error,
        },
      }

    case 'SET_ACCOUNT_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          account: action.error,
        },
      }

    case 'SET_CARD_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          cards: state.errors.cards.map((error, index) =>
            index === action.index ? action.error : error
          ),
        },
      }

    default:
      return state
  }
}

interface UseAddRequisiteFormProps {
  setValue: UseFormSetValue<NewRequisite>
}

export const useAddRequisiteForm = ({ setValue }: UseAddRequisiteFormProps) => {
  const [state, dispatch] = useReducer(formReducer, initialState)

  // Получение конфигурации текущего банка
  const bankConfig = useMemo(() => {
    if (!state.selectedBankId) return null
    return BANK_CONFIGS[state.selectedBankId as BankId] || null
  }, [state.selectedBankId])

  // Определение полей для отображения
  const fieldsToShow = useMemo(() => {
    if (!bankConfig) {
      return { showPhone: false, showAccount: false, showCards: false, showSbpPaymentLink: false, maxCards: 0 }
    }

    return {
      showPhone: bankConfig.requiredFields.includes('phone') ||
        bankConfig.optionalFields.includes('phone'),
      showAccount: bankConfig.requiredFields.includes('account') ||
        bankConfig.optionalFields.includes('account'),
      showCards: bankConfig.requiredFields.includes('cards') ||
        bankConfig.optionalFields.includes('cards'),
      showSbpPaymentLink: bankConfig.requiredFields.includes('sbpPaymentLink') ||
        bankConfig.optionalFields.includes('sbpPaymentLink'),
      maxCards: bankConfig.maxCards,
    }
  }, [bankConfig])

  // Построение реквизитов
  const buildRequisites = useCallback((cards: string[] = state.cards) => {
    if (!state.selectedBankId) return []

    const requisites = []

    // Добавляем телефон (если заполнен)
    const phoneValue = formatPhoneNumber(state.phone).cleaned
    if (phoneValue) {
      requisites.push({
        requisitType: REQUISITE_TYPES.PHONE,
        value: phoneValue,
        requisitId: '00000000-0000-0000-0000-000000000000',
      })
    }

    // Добавляем счет (если заполнен)
    const accountValue = formatBankAccount(state.account).cleaned
    if (accountValue && accountValue.length > 0) {
      requisites.push({
        requisitType: REQUISITE_TYPES.ACCOUNT,
        value: accountValue,
        requisitId: '00000000-0000-0000-0000-000000000000',
      })
    }

    // Добавляем карты (если заполнены)
    cards.forEach((card) => {
      const cardValue = formatCardNumber(card).cleaned
      if (cardValue) {
        requisites.push({
          requisitType: REQUISITE_TYPES.CARD,
          value: cardValue,
          requisitId: '00000000-0000-0000-0000-000000000000',
        })
      }
    })

    return requisites
  }, [state.selectedBankId, state.phone, state.account, state.cards])

  // Валидация поля
  const validateField = useCallback((field: 'phone' | 'account' | 'card', value: string) => {
    switch (field) {
      case 'phone': {
        const cleaned = formatPhoneNumber(value).cleaned
        if (cleaned.length > 1 && cleaned.length < 11) {
          return { message: 'Введите полный номер телефона' }
        }
        return undefined
      }
      case 'account': {
        const cleaned = formatBankAccount(value).cleaned
        if (cleaned.length > 0 && cleaned.length < 20) {
          return { message: 'Введите 20 цифр' }
        }
        return undefined
      }
      case 'card': {
        const cleaned = formatCardNumber(value).cleaned
        if (cleaned.length > 0 && cleaned.length < 16) {
          return { message: 'Введите 16 цифр' }
        }
        return undefined
      }
      default:
        return undefined
    }
  }, [])

  // Обработчики полей
  const handlePhoneChange = useCallback((value: string) => {
    const { formatted } = formatPhoneNumber(value)
    dispatch({ type: 'SET_PHONE', value: formatted })

    const error = validateField('phone', value)
    dispatch({ type: 'SET_PHONE_ERROR', error })

    setValue('createPaymentRequisitRequests', buildRequisites())
  }, [buildRequisites, setValue, validateField])

  const handleAccountChange = useCallback((value: string) => {
    const { formatted } = formatBankAccount(value)
    dispatch({ type: 'SET_ACCOUNT', value: formatted })

    const error = validateField('account', value)
    dispatch({ type: 'SET_ACCOUNT_ERROR', error })

    setValue('createPaymentRequisitRequests', buildRequisites())
  }, [buildRequisites, setValue, validateField])

  const handleCardChange = useCallback((value: string, index: number) => {
    const { cleaned } = formatCardNumber(value)
    dispatch({ type: 'SET_CARD', index, value: cleaned })

    const error = validateField('card', value)
    dispatch({ type: 'SET_CARD_ERROR', index, error })

    const newCards = [...state.cards]
    newCards[index] = cleaned
    setValue('createPaymentRequisitRequests', buildRequisites(newCards))
  }, [state.cards, buildRequisites, setValue, validateField])

  const handleSbpPaymentLinkChange = useCallback((value: string) => {
    dispatch({ type: 'SET_SBP_PAYMENT_LINK', value })
  }, [])

  // Управление картами
  const addCard = useCallback(() => {
    if (!bankConfig || state.cards.length >= bankConfig.maxCards) return
    dispatch({ type: 'ADD_CARD' })
  }, [bankConfig, state.cards.length])

  const removeCard = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_CARD', index })

    const newCards = state.cards.filter((_, i) => i !== index)
    setValue('createPaymentRequisitRequests', buildRequisites(newCards))
  }, [state.cards, buildRequisites, setValue])

  // Инициализация банка
  const initializeForBank = useCallback((bankId: string) => {
    dispatch({ type: 'SELECT_BANK', bankId })
    setValue('createPaymentRequisitRequests', [])
  }, [setValue])

  // Валидация всей формы
  const validateForm = useCallback(() => {
    if (!state.selectedBankId || !bankConfig) return false

    let isValid = true

    // Проверяем обязательные поля
    if (bankConfig.requiredFields.includes('phone')) {
      const phoneValue = formatPhoneNumber(state.phone).cleaned
      if (!phoneValue || phoneValue.length < 11) {
        dispatch({
          type: 'SET_PHONE_ERROR',
          error: { message: 'Необходимо указать номер телефона' }
        })
        isValid = false
      }
    }

    if (bankConfig.requiredFields.includes('account') || bankConfig.requiresAccount) {
      const accountValue = formatBankAccount(state.account).cleaned
      if (!accountValue || accountValue.length < 20) {
        dispatch({
          type: 'SET_ACCOUNT_ERROR',
          error: { message: 'Необходимо указать номер счета' }
        })
        isValid = false
      }
    }

    // Проверяем карты
    state.cards.forEach((card, index) => {
      if (card.trim()) {
        const cleanedCard = formatCardNumber(card).cleaned
        if (cleanedCard.length < 16) {
          dispatch({
            type: 'SET_CARD_ERROR',
            index,
            error: { message: 'Необходимо указать полный номер карты' },
          })
          isValid = false
        }
      }
    })

    return isValid
  }, [state.selectedBankId, state.phone, state.account, state.cards, bankConfig])

  // Сброс формы
  const resetForm = useCallback(() => {
    dispatch({ type: 'RESET_FORM' })
    setValue('createPaymentRequisitRequests', [])
  }, [setValue])

  return {
    // Состояние
    selectedBankId: state.selectedBankId,
    showAdditionalFields: state.showAdditionalFields,
    phone: state.phone,
    account: state.account,
    cards: state.cards,
    sbpPaymentLink: state.sbpPaymentLink,

    // Конфигурация полей
    fieldsToShow,

    // Ошибки
    phoneError: state.errors.phone?.message,
    accountError: state.errors.account?.message,
    cardsErrors: state.errors.cards.map(error => error?.message),

    // Обработчики
    handlePhoneChange,
    handleAccountChange,
    handleCardChange,
    handleSbpPaymentLinkChange,
    addCard,
    removeCard,

    // Утилиты
    validateForm,
    resetForm,
    initializeForBank,
    buildRequisites: () => buildRequisites(),

    // Дополнительные утилиты
    bankConfig,
    canAddCard: bankConfig ? state.cards.length < bankConfig.maxCards : false,
  }
}